import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { EventPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useFavorites } from "@/lib/favorites-context";
import { usePinFilters } from "@/lib/pins-context";
import { EventsFilter } from "@/components/events/events-filter";
import { FavoriteSchoolButton } from "@/components/favorite-school-button";
import { SchoolCrest } from "@/components/school-crest";
import { buttonVariants } from "@/components/ui/button";
import { ResponsiveTable, type CardColumn } from "@/components/responsive-table";
import { Eye } from "lucide-react";
import {
  filterEvents,
  usesCalendarFeed,
  parseScope,
  resolveDefaultScope,
  visibleScopes,
  type EventFilterState,
  type SchoolSets,
} from "@/lib/event-filter";

function ymd(d: Date): string {
  return d.toISOString().split("T")[0];
}

/**
 * 130 Spectator Filter -- the parent/spectator timetable. One filter strip
 * (My Kids / My Schools / My Favourites / All) + sport picker + search over a
 * cross-school events list. Personal scopes draw from /me/calendar (kids'
 * participation + followed schools); "All" draws from the global /events list.
 */
export default function MyEventsPage() {
  const { user } = useAuth();
  const { favoriteSchoolIds } = useFavorites();
  const { activeSchoolIds } = usePinFilters();
  const [searchParams, setSearchParams] = useSearchParams();

  const [calendarEvents, setCalendarEvents] = useState<EventPublic[]>([]);
  const [allEvents, setAllEvents] = useState<EventPublic[]>([]);
  const [kidSchoolIds, setKidSchoolIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Role schools come straight from the auth context (scope_type === "school").
  const roleSchoolIds = useMemo(
    () =>
      new Set(
        (user?.roles ?? [])
          .filter((r) => r.scope_type === "school")
          .map((r) => r.scope_id),
      ),
    [user],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const today = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 180);
    Promise.all([
      api.getMyCalendar(ymd(today), ymd(end)),
      api.getAllEvents({ when: "upcoming" }),
    ])
      .then(([calendar, all]) => {
        if (cancelled) return;
        setCalendarEvents(calendar.events);
        setKidSchoolIds(new Set(calendar.children.map((c) => c.school_id)));
        setAllEvents(all.events);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof api.ApiError ? e.message : "Failed to load events");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sets: SchoolSets = useMemo(
    () => ({ kidSchoolIds, favSchoolIds: favoriteSchoolIds, roleSchoolIds }),
    [kidSchoolIds, favoriteSchoolIds, roleSchoolIds],
  );

  const scopes = useMemo(() => visibleScopes(sets), [sets]);

  // Filter state from the URL; scope falls back to the role default.
  const scope = parseScope(searchParams.get("scope")) ?? resolveDefaultScope(sets);
  const sportsParam = searchParams.get("sports");
  const filterState: EventFilterState = {
    scope,
    sports: sportsParam ? sportsParam.split(",").filter(Boolean) : [],
    q: searchParams.get("q") ?? "",
  };

  function setFilter(next: EventFilterState) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("scope", next.scope);
        if (next.sports.length > 0) params.set("sports", next.sports.join(","));
        else params.delete("sports");
        if (next.q) params.set("q", next.q);
        else params.delete("q");
        return params;
      },
      { replace: true },
    );
  }

  const activeEvents = usesCalendarFeed(scope) ? calendarEvents : allEvents;

  const availableSports = useMemo(
    () =>
      [...new Set(activeEvents.map((e) => e.sport_name).filter((s): s is string => !!s))].sort(),
    [activeEvents],
  );

  const filteredEvents = useMemo(() => {
    let result = filterEvents(activeEvents, filterState, sets);
    // Pins narrow "My Schools" to the pinned set (consistent with list pages).
    if (filterState.scope === "my_schools" && activeSchoolIds.length > 0) {
      const pinned = new Set(activeSchoolIds);
      result = result.filter(
        (e) => pinned.has(e.school_id) || e.schools.some((s) => pinned.has(s.school_id)),
      );
    }
    return result;
  }, [activeEvents, filterState, sets, activeSchoolIds]);

  const state = loading ? "loading" : error ? "error" : filteredEvents.length === 0 ? "empty" : "ready";

  // 185: one column source drives the desktop table and the mobile cards.
  const columns: CardColumn<EventPublic>[] = [
    {
      key: "name",
      header: "Name",
      cardRole: "title",
      className: "font-medium",
      render: (event) => event.name,
    },
    {
      key: "school",
      header: "School",
      render: (event) => (
        <Link
          to={`/schools/${event.school_id}`}
          className="flex items-center gap-2 text-muted-foreground hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <SchoolCrest
            name={event.school_name}
            logoUrl={event.school_logo_url}
            primaryColor={event.school_primary_color}
            size={22}
          />
          <span>{event.school_name}</span>
        </Link>
      ),
    },
    {
      key: "sport",
      header: "Sport",
      className: "text-muted-foreground",
      render: (event) => event.sport_name ?? "-",
    },
    {
      key: "date",
      header: "Date / Time",
      className: "text-muted-foreground",
      render: (event) => new Date(event.starts_at).toLocaleString(),
    },
    {
      key: "matches",
      header: "Matches",
      className: "text-muted-foreground",
      render: (event) => event.match_count,
    },
    {
      key: "actions",
      header: "",
      headerClassName: "w-20",
      cardRole: "action",
      render: (event) => (
        <div className="flex gap-1">
          <FavoriteSchoolButton
            schoolId={event.school_id}
            schoolName={event.school_name}
            variant="icon"
          />
          <Link
            to={`/events/${event.id}`}
            className={buttonVariants({ variant: "ghost", size: "sm" })}
            aria-label="View event"
          >
            <Eye className="h-4 w-4" />
          </Link>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6" data-page="my-events" data-state={state}>
      <div>
        <h1 className="text-2xl font-semibold">Events</h1>
        <p className="text-sm text-muted-foreground">Your timetable across schools</p>
      </div>

      <EventsFilter
        value={filterState}
        onChange={setFilter}
        scopes={scopes}
        availableSports={availableSports}
      />

      {loading && <p className="text-muted-foreground">Loading...</p>}
      {error && (
        <p className="text-sm text-destructive" data-error>
          {error}
        </p>
      )}

      {!loading && !error && filteredEvents.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-12">
          <p className="text-muted-foreground">No matching events</p>
        </div>
      )}

      {!loading && !error && filteredEvents.length > 0 && (
        <div data-region="results">
          <ResponsiveTable
            columns={columns}
            rows={filteredEvents}
            rowKey={(event) => event.id}
            rowProps={(event) => ({ "data-item": "event", "data-item-id": event.id })}
          />
        </div>
      )}
    </div>
  );
}
